import { businessNotesAll } from "common/types";
import { useAuth, useI18N, useCloseMapperNote } from "common/hooks";
import { useEffect, useState, useMemo } from "react";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";
import { StandardButton } from "@/components/common/StandardButton";
import { ArrowBigUp, ArrowBigDown, MapPin, Timer } from "lucide-react";
import { enableShowVoteCounts } from "common/config";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";

type VenueNoteView = {
  note: businessNotesAll;
  onRefresh: () => void;
  onEdit: () => void;
  onFlagNote: () => void;
};

function VenueNoteView({ note, onRefresh, onEdit, onFlagNote }: VenueNoteView) {
  const { isMapper } = useAuth();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const showToast = useCustomToast();

  const {
    closeNote,
    error: closeNoteError,
    isLoading: closeNoteLoading,
    data: closeNoteData,
  } = useCloseMapperNote();
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);

  //resolve mapper ntoe
  useEffect(() => {
    if (closeNoteError) {
      console.log(`closeNote error: ${closeNoteError}`);
    }
  }, [closeNoteError]);

  useEffect(() => {
    if (closeNoteData) {
      console.log(`closeNote data: ${closeNoteData.message}`);
      setIsResolveModalOpen(false);
      showToast(closeNoteData.message, "success");
      setTimeout(() => {
        onRefresh();
      }, 1000);
    }
  }, [closeNoteData]);

  const formatedDateShort = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatedDateTimeShort = (date: string) => {
    const d = new Date(date);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Ensures AM/PM format
    });
  };

  // Calculate vote counts for Live Alerts
  const voteCounts = useMemo(() => {
    if (enableShowVoteCounts === "false") {
      return null;
    }

    if (
      note.action_title !== "Live Alerts" ||
      !note.votes ||
      !Array.isArray(note.votes)
    ) {
      return null;
    }

    const ongoing = note.votes.filter((vote) => vote.type === "Ongoing").length;
    const resolved = note.votes.filter(
      (vote) => vote.type === "Resolved",
    ).length;

    return { ongoing, resolved, total: ongoing + resolved };
  }, [note.action_title, note.votes]);

  return (
    <>
      <Card className="mb-2">
        <CardHeader>
          <CardTitle>
            <div className="w-full flex justify-between items-start gap-4">
              <div className="flex-1 flex flex-col justify-start items-start">
                <div className="self-stretch justify-center ucfirst text-fg-neutral-strong label-l-bold">
                  {note.note_type === "Basic Mapper Note"
                    ? note.action_title
                    : note.business_note_title}
                </div>

                {note.location_name && note.location_name !== "N/A" && (
                  <div className="w-full py-2 flex justify-start items-center gap-2">
                    <MapPin
                      className="w-4 h-4 flex-shrink-0 text-fg-neutral-subtle"
                    />
                    <span className="label-s-medium text-fg-neutral-subtle">
                      {note.location_name}
                    </span>
                  </div>
                )}

                {note.note_type === "Basic Mapper Note" ? (
                  <>
                    {note.business_notes_action_id === 15 &&
                    note.action_on_date &&
                    !note.return_on_date ? (
                      <div className="self-stretch justify-center text-fg-neutral-subtle label-s-medium">
                        {" "}
                        make venue not visible to mappers until{" "}
                        {formatedDateTimeShort(note.action_on_date)}
                      </div>
                    ) : note.business_notes_action_id === 8 &&
                      note.return_on_date ? (
                      <div className="self-stretch justify-center text-fg-neutral-subtle label-s-medium">
                        {" "}
                        on {formatedDateTimeShort(note.return_on_date)}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    {(note.schedule_date_from && note.schedule_date_to) ||
                    note.schedule_date_from ? (
                      <div className="w-full py-2 flex justify-start items-center gap-2">
                            <Timer 
                         className="w-4 h-4 flex-shrink-0 text-fg-neutral-subtle"
                       />
                       <span className="label-s-medium text-fg-neutral-subtle">
                        {note.schedule_date_from && note.schedule_date_to
                          ? `${note.formatedScheduleDateFrom} - ${note.formatedScheduleDateTo}`
                          : `${note.formatedScheduleDateFrom} - No end date`}
                        </span>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <div className="flex justify-end items-center flex-shrink-0">
                {note.note_type === "Basic Mapper Note" ? (
                  <>
                    <StandardButton
                      disabled={closeNoteLoading}
                      onClick={() => {
                        setIsResolveModalOpen(true);
                      }}
                      variant="icon"
                      size="icon"
                      className="w-10 h-10 bg-container-brand-minimal border border-border-token-brand-minimal rounded-lg hover:bg-bg-brand-ghost-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      imageSrc={
                        isDark
                          ? "/images/CheckCircleWhiteBg_Dark.svg"
                          : "/images/CheckCircleWhiteBg.svg"
                      }
                      imageAlt="Resolve note"
                      imageContainerClassName="w-5 h-5"
                      aria-label="Resolve note"
                    />
                  </>
                ) : (
                  <>
                    {isMapper && (
                      <div className="flex flex-row gap-2">
                        <StandardButton
                          variant="icon"
                          size="icon"
                          className="w-10 h-10 bg-container-brand-minimal border border-border-token-brand-minimal rounded-lg hover:bg-bg-brand-ghost-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={closeNoteLoading}
                          onClick={() => {
                            onEdit();
                          }}
                          imageSrc={
                            isDark
                              ? "/images/PencilSimple_dark.svg"
                              : "/images/PencilSimple_Primary500.svg"
                          }
                          imageAlt="Edit note"
                          imageContainerClassName="w-5 h-5"
                          aria-label="Edit note"
                        />
                      </div>
                    )}

                    {!isMapper && (
                      <div className="flex flex-row gap-2">
                        <StandardButton
                          variant="icon"
                          size="icon"
                          className="w-10 h-10 bg-container-brand-minimal border border-border-token-brand-minimal rounded-lg hover:bg-bg-brand-ghost-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={closeNoteLoading}
                          onClick={() => {
                            onFlagNote();
                          }}
                          imageSrc={
                            isDark
                              ? "/images/Flag_red.svg"
                              : "/images/Flag_red.svg"
                          }
                          imageAlt="Flag note"
                          aria-label="Flag note"
                          imageContainerClassName="w-5 h-5"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardTitle>
        </CardHeader>        
        <CardFooter className="flex-col gap-2">
          <div className="w-full min-h-11 py-2 flex flex-col justify-center items-center gap-1">
            <div className="w-full justify-center text-fg-neutral-subtle label-s-medium">
              {note.note_type === "Basic Mapper Note" ? (
                <>
                  {formatedDateShort(note.created_at)} • {note.userName}
                </>
              ) : (
                <>
                  {note.formatedCreatedAt} •{" "}
                  {isMapper ? note.business_note_by_user : "Sociability team"}
                </>
              )}
            </div>
            {note.business_note_text && (
              <div className="w-full min-h-6 flex flex-col justify-center items-start label-m-medium text-fg-neutral-strong">
                <div className="w-full justify-center ucfirst label-m-medium text-fg-neutral-strong">
                  {note.business_note_text}
                </div>
              </div>
            )}
          </div>

          {/* Vote counts for Live Alerts */}
          {enableShowVoteCounts === "true" && voteCounts && (
            <div className="w-full py-2 flex justify-start items-center gap-4 border-t border-border-token-brand-minimal">
              <div className="flex items-center gap-1.5">
                <ArrowBigUp className="w-5 h-5 text-fg-brand-strong" />
                <span className="label-m-medium text-fg-neutral-strong">
                  {voteCounts.ongoing} • Ongoing
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <ArrowBigDown className="w-5 h-5 text-fg-brand-strong" />
                <span className="label-m-medium text-fg-neutral-strong">
                  {voteCounts.resolved} • Resolved
                </span>
              </div>
            </div>
          )}
        </CardFooter>
      </Card>

      {closeNoteLoading && <Loader />}

      <ModalWithAction
        isModalOpen={isResolveModalOpen}
        title={i18n.get("resolveNote")}
        subtitle={i18n.get("areYouSureYouWantToResolveThisNote")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("resolveNoteButton")}
        onClose={() => {
          setIsResolveModalOpen(false);
        }}
        onClick={() => {
          // For Basic Mapper Note type, use the id property
          // For other note types, this button shouldn't be shown
          if (note.note_type === "Basic Mapper Note") {
            closeNote({ issueId: note.business_note_text_id.toString() });
          }
        }}
        type="warning"
        actionButtonType="warning"
      />
    </>
  );
}

export default VenueNoteView;
